/*
 *   Copyright (c) 2020 BuserNet Consulting LLC
 *   All rights reserved.
 */
import Wkt from 'wicket';
import _forEach from 'lodash/forEach';
import ConfigDeviceSettings from '../config/device.config';
import etdService from './etd.service';
import rulesetService from './ruleset.service';
import routeProfileDataSource from '../views/Routeprofile/Routeprofile.data';
import searchHistoryService from './searchHistory.service';
import gridViewDataSource from '../views/Gridview/Gridview.data';
import mapViewDataSource from '../views/Leafletmapview/Leafletmapview.data';
import globeViewDataSource from '../views/Mapview/Mapview.data';
import utilityService from './utility.service';
import x2jsService from './x2js.service';
import testdataService from './testdata.service';
import dashboardDataSource from '../views/Airport/Airport.data';
import d3 from '../globalexports/d3.export';
import bnChartDataProcessService from './chartDataProcess.service';
import { getElevationFromLocalstorage, setElevationToLocalstorage } from './elevation-cache';
import ChartConst from '../constants/chart.constant';
import DefConst from '../constants/def.constant';
import AppConst from '../constants/app.constant';
import { meteoProcessData } from './chartData/meteoProcessData';
import httpService from './http.service';
import reportError from './error-handler/reportError';
import { getServiceHostPreference } from './utils/getServiceHostPreference';
import { doRenamingCigVis } from './chartData/doRenamingCigVis';

const bnChartDataService = () => {
  const contents = {};

  function getProjection(settings) {
    // For routeprofile request there are START and END parameter
    // start, mid1, mid2, end, width, ruleset, product
    // console.log(datamode, settings);
    return httpService.getProjectionV2(
      settings.start,
      settings.mid1,
      settings.mid2,
      settings.end,
      null,
      AppConst.requestProductAll,
    );
  }

  function getRouteProfileAirportRoute(data) {
    const result = [];
    _forEach(data, (item) => {
      result.push(item.icao_id);
    });
    return result;
  }

  function findIsMavOrLampFLight(departureTime, totalFlightDuration) {
    const currentDate = new Date();
    const hoursFlightStartAfterCurrent = Math.abs(departureTime - currentDate) / 36e5;
    const hoursFlightEndAfterCurrent = hoursFlightStartAfterCurrent + totalFlightDuration;
    if (hoursFlightStartAfterCurrent <= 24 && hoursFlightEndAfterCurrent <= 24) {
      // If flight starts within 24 hrs from now (current time)
      // And If flight ends also within 24 hrs from now (current time)
      return ChartConst.products.lamp.name;
    }
    if (
      hoursFlightStartAfterCurrent <= 24
      && hoursFlightEndAfterCurrent > 24
      && hoursFlightEndAfterCurrent <= 72
    ) {
      // If flight starts within 24 hrs from now (current time)
      // And If flight ends after 24 hrs from now (current time)
      return ChartConst.products.mav.name;
    }
    if (hoursFlightStartAfterCurrent > 24 && hoursFlightEndAfterCurrent <= 72) {
      // If flight starts after 24 hrs from now (current time)
      // And If flight ends after 24 hrs from now (current time)
      return ChartConst.products.mav.name;
    }
    if (hoursFlightStartAfterCurrent > 72 || hoursFlightEndAfterCurrent > 72) {
      // If flight starts after 72 hrs from now (current time)
      // OR If flight ends after 72 hrs from now (current time)
      return 'NA';
    }
    return null;
  }

  contents.isMavOrLampFlight = (
    departureTime,
    totalFlightDuration,
  ) => findIsMavOrLampFLight(departureTime, totalFlightDuration);

  contents.getRequiredData = (
    settings,
    datamode,
  ) => httpService.http_get(getProjection(settings, datamode));
  contents.attachFlightRuleData = (flightRuleObjectMain, data, settings) => {
    // Attaching flightrules to lamp data
    _forEach(data.lamp, (airportitem, airport_key) => {
      _forEach(airportitem.forecast, (forecast, forecast_key) => {
        // forecast.flight_ruleset_data = flightRuleObjectMain.lamp[airport_key]
        // .flightRuleData[forecast_key];
        // forecast.cell_color = flightRuleObjectMain.lamp[airport_key]
        // .flightRuleData[forecast_key].cell_color;
        // forecast.cell_lbl = flightRuleObjectMain.lamp[airport_key]
        // .flightRuleData[forecast_key].cell_lbl;
        forecast.frlServerValue = forecast.frl;
        forecast.frl = flightRuleObjectMain.lamp[airport_key].flightRuleData[
          forecast_key].rule_level;
        forecast.frl = DefConst.factoryRuleSetMap[settings.ruleset].clr.length - 1 - forecast.frl;
      });
    });
    // Attaching flightrules to mav data
    _forEach(data.mav, (airportitem, airport_key) => {
      _forEach(airportitem.forecast, (forecast, forecast_key) => {
        // forecast.flight_ruleset_data = flightRuleObjectMain.mav[airport_key]
        // .flightRuleData[forecast_key];
        // forecast.cell_color = flightRuleObjectMain.mav[airport_key]
        // .flightRuleData[forecast_key].cell_color;
        // forecast.cell_lbl = flightRuleObjectMain.mav[airport_key]
        // .flightRuleData[forecast_key].cell_lbl;
        forecast.frlServerValue = forecast.frl;
        forecast.frl = flightRuleObjectMain.mav[airport_key].flightRuleData[
          forecast_key].rule_level;
        forecast.frl = DefConst.factoryRuleSetMap[settings.ruleset].clr.length - 1 - forecast.frl;
      });
    });
  };

  function getFlightRuleDataMeteo(settings, data) {
    const settingMav = utilityService.clone(settings);
    const settingLamp = utilityService.clone(settings);
    settingMav.product = ChartConst.products.mav.name;
    settingLamp.product = ChartConst.products.lamp.name;
    const flightRuleObjectMain = {
      mav: rulesetService.getRuleSetData(data.mav, settingMav),
      lamp: rulesetService.getRuleSetData(data.lamp, settingLamp),
    };
    _forEach(data.lamp.forecast, (forecast, forecast_key) => {
      // forecast.flight_ruleset_data = flightRuleObjectMain.lamp[airport_key]
      // .flightRuleData[forecast_key];
      // forecast.cell_color = flightRuleObjectMain.lamp[airport_key]
      // .flightRuleData[forecast_key].cell_color;
      // forecast.cell_lbl = flightRuleObjectMain.lamp[airport_key]
      // .flightRuleData[forecast_key].cell_lbl;
      forecast.frl = flightRuleObjectMain.lamp.flightRuleData[forecast_key].rule_level;
      forecast.frl = DefConst.factoryRuleSetMap[settings.ruleset].clr.length - 1 - forecast.frl;
    });
    _forEach(data.mav.forecast, (forecast, forecast_key) => {
      // forecast.flight_ruleset_data = flightRuleObjectMain.mav[airport_key]
      // .flightRuleData[forecast_key];
      // forecast.cell_color = flightRuleObjectMain.mav[airport_key]
      // .flightRuleData[forecast_key].cell_color;
      // forecast.cell_lbl = flightRuleObjectMain.mav[airport_key]
      // .flightRuleData[forecast_key].cell_lbl;
      forecast.frl = flightRuleObjectMain.mav.flightRuleData[forecast_key].rule_level;
      forecast.frl = DefConst.factoryRuleSetMap[settings.ruleset].clr.length - 1 - forecast.frl;
    });
    return flightRuleObjectMain;
  }
  contents.getFlightRuleData = (settings, data) => {
    settings.ruleset = utilityService.getRulesetValue();
    if (settings.dataMode !== 'routeprofile') {
      getFlightRuleDataMeteo(settings, data);
    } else {
      const settingMav = utilityService.clone(settings);
      const settingLamp = utilityService.clone(settings);
      const flightRuleObjectMain = {
        mav: [],
        lamp: [],
      };
      settingMav.product = ChartConst.products.mav.name;
      settingLamp.product = ChartConst.products.lamp.name;
      _forEach(data.lamp, (airportitem) => {
        flightRuleObjectMain.lamp.push(rulesetService.getRuleSetData(airportitem, settingLamp));
      });
      _forEach(data.mav, (airportitem) => {
        flightRuleObjectMain.mav.push(rulesetService.getRuleSetData(airportitem, settingMav));
      });
      // contents.attachFlightRuleData(flightRuleObjectMain)
      contents.attachFlightRuleData(flightRuleObjectMain, data, settings);
      // if (factory.flightRules.mav && factory.flightRules.lamp) {
      //     flightRuleObjectMain = factory.flightRules;
      // } else {
      //     settingMav.product = ChartConst.products.mav.name;
      //     settingLamp.product = ChartConst.products.lamp.name;
      //     _forEach(factory.fullData.lamp, function (airportitem) {
      //         flightRuleObjectMain.lamp.push(rulesetService
      // .getRuleSetData(airportitem, settingLamp));
      //     });
      //     _forEach(factory.fullData.mav, function (airportitem) {
      //         flightRuleObjectMain.mav.push(rulesetService
      // .getRuleSetData(airportitem, settingMav));
      //     });
      // }
      // factory.setFlightRule(flightRuleObjectMain);
      return flightRuleObjectMain;
    }
    return null;
  };
  contents.getWeatherApi = async (apiToUse, lat, lng) => {
    switch (apiToUse) {
      case AppConst.possibleWeatherApi.govWeather:
        return httpService.getApiWeatherGovProjection(lat, lng);
      case AppConst.possibleWeatherApi.mapClick:
        return httpService.getForecastWeatherGovProjection(lat, lng);
      default:
    }
  };

  contents.getWeatherData = async (settings, apiToUse) => {
    if (settings.airportDetails) {
      const weatherApi = await contents.getWeatherApi(
        apiToUse,
        settings.airportDetails.lat,
        settings.airportDetails.lng,
      );
      if (weatherApi) {
        let weatherData = await httpService.http_get(weatherApi, null, true);
        if (weatherData && weatherData.success === false) {
          // forecast.map sends success = false when any error
          weatherData = null;
        }
        //searchHistoryService.setAirportHistoryLog(AppConst.dataMode.weekly, weatherData, settings);
        return weatherData;
      }
      // if weather api is not available then show service not available.
      const weatherData = {};
      weatherData.serviceUnavailable = true;
      weatherData.success = false;
      return weatherData;
    }
    return null;
  };
  contents.roundOfPrecip = (value, isSevereThunderStorm) => {
    let roundOffTo = 10;
    let thresholdToUse = ChartConst.precipMinThresholdTooltip;
    if (isSevereThunderStorm) {
      roundOffTo = 5;
      thresholdToUse = ChartConst.st6MinThresholdTooltip;
    }
    if (value !== null) {
      const r = value % roundOffTo;
      if (value < thresholdToUse) {
        return ChartConst.precipNoneValuePlaceHolder;
      }
      if (value && value > thresholdToUse && r > 0) {
        return value + (roundOffTo - r);
      }
    }
    return value;
  };

  function preProcessMeteoData(data) {
    const versionToUse = ConfigDeviceSettings.settings.apiVersionKey;
    if (data.forecast) {
      _forEach(data.forecast, (value) => {
        value.lat = data.lat;
        value.lng = data.lng;
        value.icao_id = data.icao_id;
        value.faa_facility_name = data.faa_facility_name;
        if (versionToUse !== 3
          && value.cld
          && value.cld !== DefConst.clouds.broken
          && value.cld !== DefConst.clouds.overcast
        ) {
          value.cig = 8;
        }
        // round of wind speeds
        if (value.wsp) {
          value.wsp = parseInt(value.wsp.toFixed(0), 10);
        }
        if (value.xwd) {
          value.xwd = parseInt(value.xwd.toFixed(0), 10);
        }
        // round off precipitation percentages
        if (value.p06) {
          value.p06 = contents.roundOfPrecip(value.p06);
        }
        if (value.ppo) {
          value.ppo = contents.roundOfPrecip(value.ppo);
        }
        if (value.t06) {
          value.t06 = contents.roundOfPrecip(value.t06);
        }
        if (value.t01) {
          value.t01 = contents.roundOfPrecip(value.t01);
        }
        if (value.st6) {
          value.st6 = contents.roundOfPrecip(value.st6, true);
        }
        if (value.sno) {
          value.sno = contents.roundOfPrecip(value.sno);
        }
        if (value.cp2) {
          value.cp2 = contents.roundOfPrecip(value.cp2);
        }
        if (value.fzp) {
          value.fzp = contents.roundOfPrecip(value.fzp);
        }
        // console.log(contents.roundOfPrecip(82));
        // if (value.tmp === null && value.wsp === null) {
        //     value.p06 = null;
        //     value.ppo = null;
        //     value.q06 = null;
        //     value.t06 = null;
        //     value.t01 = null;
        //     value.cp2 = null;
        //     value.st6 = null;
        //     value.sno = null;
        //     value.fzp = null;
        //     value.cig = null;
        //     value.vis = null;
        //     value.wgs = null;
        //     value.xwd = null;
        // }
        value.original_forecast_ts = value.forecast_ts;
        value.forecast_ts_local = new Date(`${value.forecast_ts}Z`); // forecast_ts local time
        value.forecast_ts_utc = new Date(value.forecast_ts); // forecast_ts zulu time
        value.sunMoonPositionData = utilityService.getSunMoonCalculation(
          value.forecast_ts_local,
          data.lat,
          data.lng,
        );
      });
      data.forecast.sort(utilityService.sortByForecastTs);
      if (data.forecast.length && data.forecast[data.forecast.length - 2].t06 === null) {
        data.forecast[data.forecast.length - 2].t06 = data.forecast[data.forecast.length - 1].t06;
      }
      if (data.forecast.length && data.forecast[data.forecast.length - 2].st6 === null) {
        data.forecast[data.forecast.length - 2].st6 = data.forecast[data.forecast.length - 1].st6;
      }
    }
    return data;
  }
  contents.getSliderRangeSource = (totalFlightDuration, lampStartAt, mavEndsAt) => {
    const sliderMinutesRange = ChartConst.travelTimeRangeMinutes - totalFlightDuration * 60;
    const startLampFrom = lampStartAt;
    return utilityService.getAllDatesInNextXMinutes(
      sliderMinutesRange,
      mavEndsAt,
      totalFlightDuration,
      30,
      startLampFrom,
    );
  };

  function preProcessRouteProfileData(
    data,
    settings,
    calculateFlightData,
    dataMode,
    dataType,
    allData,
  ) {
    let i = 0;
    let numCol = ChartConst.products.lamp.num_column;
    if (dataType === ChartConst.products.mav.name) {
      numCol = ChartConst.products.mav.num_column;
    }
    const indexes = {
      start: null,
      mid1: null,
      mid2: null,
      end: null,
    };
    let idxHavingForecast = -1;
    while (i < data.length) {
      if (data[i].forecast) {
        idxHavingForecast = i;
        break;
      }
      i += 1;
    }
    // Stripping all the airports with forecast as null
    _forEach(data.slice().reverse(), (item) => {
      if (!item.forecast && idxHavingForecast >= 0) {
        // data.splice(object.length - 1 - index, 1);
        // if forecast of any airport is null then
        item.forecast = [];
        i = 0;
        while (i < numCol) {
          if (dataType === ChartConst.products.mav.name) {
            item.forecast.push(
              utilityService.getDummyMavNode(data[idxHavingForecast].forecast[i].forecast_ts),
            );
          } else {
            item.forecast.push(
              utilityService.getDummyLampNode(data[idxHavingForecast].forecast[i].forecast_ts),
            );
          }
          i += 1;
        }
      }
    });
    const flightDirections = contents.getFlightDirections(settings);
    _forEach(data, (airport, key) => {
      if (airport.icao_id === settings.start) {
        indexes.start = key;
      }
      if (airport.icao_id === settings.mid1) {
        indexes.mid1 = key;
      }
      if (airport.icao_id === settings.mid2) {
        indexes.mid2 = key;
      }
      if (airport.icao_id === settings.end) {
        indexes.end = key;
      }
    });
    // check if we can get rid of this for loop
    _forEach(data, (airport, key) => {
      airport.travel_time = airport.elapsed_nm / settings.groundSpeed;
      const [flightDirections0, flightDirections1, flightDirections2] = flightDirections;
      if (settings.mid1 && settings.mid2) {
        if (key < indexes.mid1) {
          airport.flightDirection = flightDirections0;
        } else if (key >= indexes.mid1 && key < indexes.mid2) {
          airport.flightDirection = flightDirections1;
        } else {
          airport.flightDirection = flightDirections2;
        }
      } else if (settings.mid1 && !settings.mid2) {
        if (key < indexes.mid1) {
          airport.flightDirection = flightDirections0;
        } else if (key >= indexes.mid1 && key <= indexes.end) {
          airport.flightDirection = flightDirections1;
        }
      } else if (!settings.mid1 && settings.mid2) {
        if (key < indexes.mid2) {
          airport.flightDirection = flightDirections0;
        } else if (key >= indexes.mid2 && key <= indexes.end) {
          airport.flightDirection = flightDirections1;
        }
      } else {
        airport.flightDirection = flightDirections0;
      }
      if (airport.forecast && airport.forecast.length) {
        if (dataType === ChartConst.products.mav.name) {
          airport.forecast.length = ChartConst.products.mav.num_column;
        } else if (dataType === ChartConst.products.lamp.name) {
          airport.forecast.length = ChartConst.products.lamp.num_column;
        }
      }
      _forEach(airport.forecast, (forecast) => {
        forecast.lat = airport.lat;
        forecast.lng = airport.lng;
        forecast.original_forecast_ts = forecast.forecast_ts;
        const forecast_ts_local = new Date(`${forecast.forecast_ts}Z`);
        forecast.forecast_ts_local = forecast_ts_local; // forecast_ts local time
        forecast.forecast_ts_utc = new Date(forecast.forecast_ts); // forecast_ts zulu time
        forecast.forecast_ts = forecast_ts_local;
        forecast.travel_time = airport.elapsed_nm / settings.groundSpeed;
        forecast.icao_id = airport.icao_id;
        forecast.faa_facility_name = airport.faa_facility_name;
        forecast.q06_mapped = ChartConst.mavDefsMap.q06[forecast.q06];
        forecast.elapsed_nm = airport.elapsed_nm;
        forecast.crosstrack_nm = airport.crosstrack_nm;
        forecast.total_nm = airport.total_nm;
        // round of wind speeds
        if (forecast.wsp) {
          forecast.wsp = parseInt(forecast.wsp.toFixed(0), 10);
        }
        if (forecast.xwd) {
          forecast.xwd = parseInt(forecast.xwd.toFixed(0), 10);
        }
        if (forecast.p06) {
          forecast.p06 = contents.roundOfPrecip(forecast.p06);
        }
        if (forecast.ppo) {
          forecast.ppo = contents.roundOfPrecip(forecast.ppo);
        }
        if (forecast.t06) {
          forecast.t06 = contents.roundOfPrecip(forecast.t06);
        }
        if (forecast.t01) {
          forecast.t01 = contents.roundOfPrecip(forecast.t01);
        }
        if (forecast.st6) {
          forecast.st6 = contents.roundOfPrecip(forecast.st6, true);
        }
        if (forecast.sno) {
          forecast.sno = contents.roundOfPrecip(forecast.sno);
        }
        if (forecast.cp2) {
          forecast.cp2 = contents.roundOfPrecip(forecast.cp2);
        }
        if (forecast.fzp) {
          forecast.fzp = contents.roundOfPrecip(forecast.fzp);
        }
      });
      if (airport.forecast) {
        airport.forecast.sort(utilityService.sortByForecastTs);
        if (airport.forecast.length && airport.forecast[airport.forecast.length - 2].t06 === null) {
          airport.forecast[airport.forecast.length - 2].t06 = airport.forecast[
            airport.forecast.length - 1].t06;
        }
      }
    });
    if (
      dataType === 'MAV'
      && allData.lamp
      && allData.lamp[0]
      && allData.lamp[0].forecast
      && allData.mav[0]
      && allData.mav[0].forecast
    ) {
      const lampStartAt = utilityService.roundTimeToNearest30Minutes();
      const mavEndsAt = allData.mav[0].forecast[
        allData.mav[0].forecast.length - 1].forecast_ts_local;
      const sliderMainRange = contents.getSliderRangeSource(
        allData.lamp.totalFlightDuration,
        lampStartAt,
        mavEndsAt,
      );
      if (etdService) {
        settings.departureTime = etdService.getDepartureTime(
          lampStartAt,
          sliderMainRange[sliderMainRange.length - 1],
        );
      }
      if (etdService && !settings.departureTime) {
        // if due to some reason departure time is not set by lamp forecast
        settings.departureTime = etdService.getDepartureTime(
          null,
          sliderMainRange[sliderMainRange.length - 1],
        );
      }
    }
    if (allData.lamp && allData.lamp[0] && allData.lamp[0].forecast === null) {
      // for no Data available we dont need departure time to hide NAN
      delete settings.departureTime;
    }
    if (data.length && calculateFlightData) {
      data.totalDistanceToTravel = data[0].total_nm;
      // TotalFlightDuration in hour = TotalDistance / GroundSpeed
      data.totalFlightDuration = data.totalDistanceToTravel / settings.groundSpeed;
      data.finalDestinationArrivalTime = utilityService.addHours(
        new Date(settings.departureTime),
        data.totalFlightDuration,
      );
      data.flightMode = findIsMavOrLampFLight(settings.departureTime, data.totalFlightDuration);
      data.airportSequence = getRouteProfileAirportRoute(data);
    }
    return data;
  }

  function preProcessData(data, settings, datamode, dataType, alldata) {
    if (settings.testmode) {
      data = testdataService.goRandomBounds(data, datamode);
    }
    switch (datamode) {
      case AppConst.dataMode.meteogram:
        preProcessMeteoData(data, settings);
        break;
      case AppConst.dataMode.routeprofile:
      case AppConst.dataMode.leafletmapview:
      case AppConst.dataMode.gridview:
      case AppConst.dataMode.mapview:
        preProcessRouteProfileData(data, settings, true, datamode, dataType, alldata);
        break;
      default:
    }
  }
  contents.getFlightDirections = (settings) => {
    let mid1Detail;
    let mid2Detail;
    const startDetail = utilityService.getMatchedAirportObject(settings.start);
    const endDetail = utilityService.getMatchedAirportObject(settings.end);
    if (settings.mid1) {
      mid1Detail = utilityService.getMatchedAirportObject(settings.mid1);
    }
    if (settings.mid2) {
      mid2Detail = utilityService.getMatchedAirportObject(settings.mid2);
    }
    const flightDirections = [];
    if (settings.mid1 && settings.mid2) {
      flightDirections.push(utilityService.getPlaneDirection(startDetail, mid1Detail));
      flightDirections.push(utilityService.getPlaneDirection(mid1Detail, mid2Detail));
      flightDirections.push(utilityService.getPlaneDirection(mid2Detail, endDetail));
    } else if (settings.mid1 && !settings.mid2) {
      flightDirections.push(utilityService.getPlaneDirection(startDetail, mid1Detail));
      flightDirections.push(utilityService.getPlaneDirection(mid1Detail, endDetail));
    } else if (!settings.mid1 && settings.mid2) {
      flightDirections.push(utilityService.getPlaneDirection(startDetail, mid2Detail));
      flightDirections.push(utilityService.getPlaneDirection(mid2Detail, endDetail));
    } else {
      flightDirections.push(utilityService.getPlaneDirection(startDetail, endDetail));
    }
    return flightDirections;
  };
  contents.processMapMosRecords = (airport) => {
    const stampsCollection = [];
    const tempDictionary = {};
    _forEach(airport.lamp, (forecast) => {
      tempDictionary[forecast.t] = forecast.v;
    });
    if (airport.mav) {
      _forEach(airport.mav, (forecast) => {
        const t = new Date(`${forecast.t}Z`);
        forecast.forecast_ts_local = t; // forecast_ts local time
        forecast.forecast_ts = t;
        forecast.forecast_ts_utc = new Date(forecast.t);
        forecast.icao_id = airport.icao_id;
        forecast.lat = airport.lat;
        forecast.lng = airport.lng;
        if (tempDictionary[forecast.t]) {
          // for first 24hours if lamp is available then use that
          forecast.v = tempDictionary[forecast.t];
        }
        forecast.frl = ConfigDeviceSettings.settings.enableAlternateColorScheme ? null : forecast.v;
      });
      // sorting lamp records
      airport.mav.sort(utilityService.sortByForecastTs);
      _forEach(airport.mav, (forecast) => {
        stampsCollection.push(forecast.forecast_ts);
      });
    }
    if (airport.lamp) {
      _forEach(airport.lamp, (forecast) => {
        forecast.icao_id = airport.icao_id;
        forecast.lat = airport.lat;
        forecast.lng = airport.lng;
        const t = new Date(`${forecast.t}Z`);
        forecast.forecast_ts_local = t; // forecast_ts local time
        forecast.forecast_ts = t;
        forecast.forecast_ts_utc = new Date(forecast.t);
      });
      // sorting lamp records
      airport.lamp.sort(utilityService.sortByForecastTs);
      _forEach(airport.lamp, (forecast) => {
        if (forecast.v === null) {
          // If lamp record is missing then use mav record
          const closestDateObj = contents.findClosestDate(forecast.forecast_ts, stampsCollection);
          forecast.realfrl = forecast.v;
          if (
            airport
            && airport.mav
            && airport.mav[closestDateObj.index]
            && airport.mav[closestDateObj.index].v
          ) {
            forecast.v = airport.mav[closestDateObj.index].v;
            forecast.closestDateObjFromMav = closestDateObj;
            forecast.isMavFrl = true;
          }
        }
        forecast.frl = ConfigDeviceSettings.settings.enableAlternateColorScheme ? null : forecast.v;
      });
    }
  };
  contents.preProcessDataHandle = (data, settings, datamode, dataType) => {
    preProcessData(data, settings, datamode, dataType);
  };

  function getFactoryForDataMode(datamode) {
    switch (datamode) {
      case AppConst.dataMode.meteogram:
        return dashboardDataSource;
      case AppConst.dataMode.routeprofile:
        return routeProfileDataSource;
      case AppConst.dataMode.leafletmapview:
        return mapViewDataSource;
      case AppConst.dataMode.mapview:
        return globeViewDataSource;
      case AppConst.dataMode.gridview:
        return gridViewDataSource;
      default:
    }
    return null;
  }

  function normalizeAltitudeValue(value, settings, datamode) {
    let aglAppend = ChartConst.mslTooltipPostfix;
    value.altitude_min_original = value.altitude_min;
    value.altitude_max_original = value.altitude_max;
    // If altitude_min || altitude_max is null then treat it as 0, 100000
    value.altitude_min = value.altitude_min || 0;
    value.altitude_max = value.altitude_max || 100000;
    // Limiting altitude for certain hazard type
    switch (value.hazard_type) {
      case ChartConst.gairmetConfig.mtnObscn.key:
      case ChartConst.gairmetConfig.mtnObscn.key2:
      case ChartConst.gairmetConfig.ceilingVis.key:
      case ChartConst.gairmetConfig.sfcWinds.key:
      case ChartConst.gairmetConfig.sfcWinds.key2:
        // IFR and mtnObscn can go MAX 1000 without elevation
        value.isAglAirmet = true;
        aglAppend = ChartConst.aglTooltipPostfix;
        if (datamode === AppConst.dataMode.meteogram) {
          value.altitude_max = Math.min(
            settings.airportDetails.el + ChartConst.maxIFROrMtnWithourElevation,
            settings.airportDetails.el + value.altitude_max,
          );
          value.nonAglAltitude_max = Math.min(
            ChartConst.maxIFROrMtnWithourElevation,
            value.altitude_max,
          );
        } else {
          value.altitude_max = Math.min(ChartConst.maxIFROrMtnWithourElevation, value.altitude_max);
        }
        break;
      case ChartConst.gairmetConfig.llWindShear.key:
        // LLWS can go MAX 2000 without elevation
        value.isAglAirmet = true;
        aglAppend = ChartConst.aglTooltipPostfix;
        if (datamode === AppConst.dataMode.meteogram) {
          value.altitude_max = Math.min(
            settings.airportDetails.el + ChartConst.maxLLWSWithourElevation,
            settings.airportDetails.el + value.altitude_max,
          );
          value.nonAglAltitude_max = Math.min(
            ChartConst.maxLLWSWithourElevation,
            value.altitude_max,
          );
        } else {
          value.altitude_max = Math.min(ChartConst.maxLLWSWithourElevation, value.altitude_max);
        }
        break;
      default:
    }
    value.altitude_max_org_ref = value.altitude_max;
    value.altitude_tooltip_text = `${utilityService.abbreviateCeiling(
      value.altitude_min,
      ChartConst.ChartLabelSpecs.cig.tooltip.mobileUnit,
      value.isAglAirmet,
      value.hazard_type,
      'min',
    )}-${utilityService.abbreviateCeiling(
      value.nonAglAltitude_max || value.altitude_max,
      ChartConst.ChartLabelSpecs.cig.tooltip.mobileUnit,
      value.isAglAirmet,
      value.hazard_type,
      'max',
    )}${utilityService.appendAglMsl(value.isAglAirmet, value.altitude_max, aglAppend)}`;
    if (value.useSurfaceAsMinAlt) {
      value.real_altitude_tooltip_text = `${AppConst.surface}-${utilityService.abbreviateCeiling(
        value.altitude_max,
        ChartConst.ChartLabelSpecs.cig.tooltip.mobileUnit,
        value.isAglAirmet,
        value.hazard_type,
        'max',
      )}${utilityService.appendAglMsl(value.isAglAirmet, value.altitude_max, aglAppend)}`;
    } else {
      value.real_altitude_tooltip_text = `${utilityService.abbreviateCeiling(
        value.real_altitude_min,
        ChartConst.ChartLabelSpecs.cig.tooltip.mobileUnit,
        value.isAglAirmet,
        value.hazard_type,
        'min',
      )}-${utilityService.abbreviateCeiling(
        value.altitude_max,
        ChartConst.ChartLabelSpecs.cig.tooltip.mobileUnit,
        value.isAglAirmet,
        value.hazard_type,
        'max',
      )}${utilityService.appendAglMsl(value.isAglAirmet, value.altitude_max, aglAppend)}`;
    }
  }
  contents.normalizeAltitudeValue = (value, settings, datamode) => {
    normalizeAltitudeValue(value, settings, datamode);
  };

  function attachHazardTypeUniqueIdentifier(met) {
    met.hazard_type_identifier = met.hazard_type;
    switch (met.hazard_type) {
      case ChartConst.gairmetConfig.llWindShear.key:
        met.hazard_type_identifier = ChartConst.gairmetConfig.llWindShear.refKey;
        break;
      case ChartConst.gairmetConfig.ceilingVis.key:
        met.hazard_type_identifier = ChartConst.gairmetConfig.ceilingVis.refKey;
        break;
      case ChartConst.gairmetConfig.icing.key:
        met.hazard_type_identifier = ChartConst.gairmetConfig.icing.refKey;
        break;
      case ChartConst.gairmetConfig.turbLow.key:
        met.hazard_type_identifier = ChartConst.gairmetConfig.turbLow.refKey;
        break;
      case ChartConst.gairmetConfig.turbHigh.key:
        met.hazard_type_identifier = ChartConst.gairmetConfig.turbHigh.refKey;
        break;
      case ChartConst.gairmetConfig.mtnObscn.key:
      case ChartConst.gairmetConfig.mtnObscn.key2:
        met.hazard_type_identifier = ChartConst.gairmetConfig.mtnObscn.refKey;
        break;
      case ChartConst.gairmetConfig.sfcWinds.key:
      case ChartConst.gairmetConfig.sfcWinds.key2:
        met.hazard_type_identifier = ChartConst.gairmetConfig.sfcWinds.refKey;
        break;
      case ChartConst.gairmetConfig.frzgLvl.key:
      case ChartConst.gairmetConfig.frzgLvl.key2:
      case ChartConst.gairmetConfig.frzgLvl.key3:
        met.hazard_type_identifier = ChartConst.gairmetConfig.frzgLvl.refKey;
        break;
      default:
    }
  }
  contents.attachHazardTypeUniqueIdentifier = function (met) {
    attachHazardTypeUniqueIdentifier(met);
  };

  function processAirSigmentData(data, settings, datamode) {
    let airsigmet;
    let gairmet;
    switch (datamode) {
      case AppConst.dataMode.meteogram:
        // settings.closestSlice = utilityService.getCeilingMinDomain(settings.airportDetails.el);
        if (data && data.airsigmet && data.airsigmet.airsigmet) {
          airsigmet = data.airsigmet.airsigmet;
        }
        if (data && data.gairmet && data.gairmet.gairmet) {
          gairmet = data.gairmet.gairmet;
        }
        break;
      case AppConst.dataMode.gridview:
      case AppConst.dataMode.routeprofile:
      case AppConst.dataMode.leafletmapview:
      case AppConst.dataMode.mapview:
        if (data && data.airsigmet && data.airsigmet) {
          airsigmet = data.airsigmet;
        }
        if (data && data.gairmet && data.gairmet) {
          gairmet = data.gairmet;
        }
        break;
      default:
    }
    if (airsigmet) {
      // Stripping all airmet with with hazard_type as frzgLvl and airsigmet_type as AIRMET
      _forEach(airsigmet.slice().reverse(), (item, index, object) => {
        if (
          item.hazard_type === ChartConst.gairmetConfig.frzgLvl.key
          || item.airsigmet_type === 'AIRMET'
        ) {
          airsigmet.splice(object.length - 1 - index, 1);
        }
      });
      _forEach(airsigmet, (value) => {
        value.real_altitude_min = value.altitude_min;
        if (value.hazard_type === ChartConst.gairmetConfig.icing.key) {
          value.altitude_min = -1;
        }
        if (value.hazard_type === ChartConst.gairmetConfig.icing.key && value.altitude_min === -1) {
          data.waitForAloft = true;
        }
        value.isSigmet = true;
        value.properties = {};
        value.geometry = {
          type: 'Polygon',
          coordinates: [[]],
        };
        if (value.wkt) {
          const wkt = new Wkt.Wkt();
          wkt.read(value.wkt);
          if (wkt.components && wkt.components[0]) {
            // _forEach(wkt.components[0], function (xy) {
            //     value.geometry.coordinates[0].push([xy.y, xy.x]);
            // });
            value.geometry = wkt.toJson();
            value.type = 'Feature';
            if (datamode === 'mapview' && value.geometry.type === 'Polygon') {
              /**
               * This is done to make widning order of polygon right if it is anti-clockwise
               * https://bl.ocks.org/mbostock/a7bdfeb041e850799a8d3dce4d8c50c8
               */
              if (d3.geoArea(value) > 5) {
                value.geometry.coordinates[0] = value.geometry.coordinates[0].reverse();
              }
            }
            // value.geometry.coordinates[0] = value.geometry.coordinates[0].reverse();
          }
        }
        normalizeAltitudeValue(value, settings, datamode);
        attachHazardTypeUniqueIdentifier(value);
        value.key = value.hazard_type_identifier;
        value.tooltip = {};
        value.tooltip.prefix = bnChartDataProcessService.getAirmetTooltipPrefix(value);
        value.hazardConfig = bnChartDataProcessService.getGairmetColor(value);
        value.colorIdentifier = value.hazardConfig.strokeColor;
        value.properties = {
          hazard: value.key,
          severity: value.hazard_severity,
        };
        if (value.hazard_type === ChartConst.gairmetConfig.convectiveAirmet.key) {
          // for convectives remove hazard_severity
          value.hazard_severity = null;
          value.properties.severity = null;
        }
        value.altitude_min = Math.max(value.altitude_min, 0);
        value.altitude_max = Math.min(value.altitude_max, settings.maximumAltitudeAllowed);
        value.original_valid_time_from = value.valid_time_from;
        value.original_valid_time_to = value.valid_time_to;
        const valid_time_from_local = new Date(`${value.valid_time_from}Z`);
        const valid_time_to_local = new Date(`${value.valid_time_to}Z`);
        // If is sigmet then we have validTimeFrom, validTimeTo
        value.validTimeFromLocalReal = valid_time_from_local;
        value.validTimeToLocalReal = valid_time_to_local;
        value.validFromLocal = valid_time_from_local;
        value.expireOnLocal = valid_time_to_local;

        if (value.hazard_type === ChartConst.gairmetConfig.convectiveAirmet.refKey) {
          // For Convective use current time as validFrom
          const vtf = utilityService.subtractMinutes(new Date(), 100);
          value.valid_time_from_local = vtf;
          value.valid_time_from_utc = vtf;
          value.validFromLocal = vtf;
        } else {
          value.valid_time_from_local = valid_time_from_local;
          value.valid_time_from_utc = new Date(value.valid_time_from);
        }
        value.valid_time_to_local = valid_time_to_local;
        value.valid_time_to_utc = new Date(value.valid_time_to);
        value.duplicationKey = `${value.hazard_type_identifier}:${value.altitude_tooltip_text}:${value.original_valid_time_from}:${value.original_valid_time_to}:${value.wkt}`;
      });
    }
    if (gairmet) {
      _forEach(gairmet.slice().reverse(), (item, index, object) => {
        if (item.hazard_type === ChartConst.gairmetConfig.frzgLvl.key) {
          gairmet.splice(object.length - 1 - index, 1);
        }
      });
      _forEach(gairmet, (value, key) => {
        value.real_altitude_min = value.altitude_min;
        if (value.hazard_type === ChartConst.gairmetConfig.icing.key && value.altitude_min === -1) {
          data.waitForAloft = true;
        }
        value.properties = {};
        value.geometry = {
          type: 'Polygon',
          coordinates: [],
        };
        if (value.wkt) {
          const wkt = new Wkt.Wkt();
          wkt.read(value.wkt);
          if (wkt.components && wkt.components[0]) {
            _forEach(wkt.components[0], (xy) => {
              value.geometry.coordinates.push([xy.y, xy.x]);
            });
          }
        }
        const expire_time_local = new Date(`${value.expire_time}Z`);
        const valid_time_local = new Date(`${value.valid_time}Z`);
        normalizeAltitudeValue(value, settings, datamode, key);
        value.altitude_min = Math.max(value.altitude_min, 0);
        value.altitude_max = Math.min(value.altitude_max, settings.maximumAltitudeAllowed);
        value.original_valid_time = value.valid_time;
        value.original_expire_time = value.expire_time;
        value.original_expire_time_local = new Date(expire_time_local);
        value.original_valid_time_local = new Date(valid_time_local);
        attachHazardTypeUniqueIdentifier(value);
        value.key = value.hazard_type_identifier;
        value.tooltip = {};
        value.tooltip.prefix = bnChartDataProcessService.getAirmetTooltipPrefix(value);
        value.hazardConfig = bnChartDataProcessService.getGairmetColor(value);
        value.colorIdentifier = value.hazardConfig.strokeColor;
        value.properties = {
          hazard: value.key,
        };
        // If is gairmet then we have validTime
        value.validTimeFromLocalReal = new Date(valid_time_local);
        value.validFromLocal = utilityService.subtractHours(
          new Date(valid_time_local),
          ChartConst.gairmetConfig.gairmetOffsetHoursPast,
        );
        value.expireOnLocal = utilityService.addHours(
          new Date(valid_time_local),
          ChartConst.gairmetConfig.gairmetOffsetHoursFuture,
        );
        value.valid_time_local = utilityService.subtractHours(
          new Date(valid_time_local),
          ChartConst.gairmetConfig.gairmetOffsetHoursPast,
        );
        value.expire_time_local = utilityService.addHours(
          new Date(valid_time_local),
          ChartConst.gairmetConfig.gairmetOffsetHoursFuture,
        );
        value.valid_time_utc = utilityService.subtractHours(
          new Date(value.valid_time),
          ChartConst.gairmetConfig.gairmetOffsetHoursPast,
        );
        value.expire_time_utc = utilityService.addHours(
          new Date(value.valid_time),
          ChartConst.gairmetConfig.gairmetOffsetHoursFuture,
        );
        value.duplicationKey = `${value.hazard_type_identifier}:${value.altitude_tooltip_text}:${value.original_valid_time}:${value.wkt}`;
      });
    }
  }

  function refillAtIndex(data, idx) {
    let i = 0;
    data.lamp[idx].forecast = utilityService.clone(data.mav[idx].forecast);
    while (i < 4) {
      if (data.lamp[idx] && data.lamp[idx].forecast) {
        data.lamp[idx].forecast.push(utilityService.clone(data.mav[idx].forecast[0]));
      }
      i += 1;
    }
  }

  function refillLampStartEnd(data) {
    if (data.lamp && data.lamp[0]) {
      if (data.lamp[0].forecast === null) {
        refillAtIndex(data, 0);
      }
      if (data.lamp[data.lamp.length - 1].forecast === null) {
        refillAtIndex(data, data.lamp.length - 1);
      }
    }
  }

  function removePastMavRecords(data) {
    if (!data.lamp || !data.lamp.forecast || !data.mav || !data.mav.forecast) {
      // return if no lamp data
      return;
    }
    let i = 0;
    let numberOfPops = 0;
    while (i < data.mav.forecast.length) {
      if (data.mav.forecast[i].forecast_ts_local >= data.lamp.forecast[0].forecast_ts_local) {
        break;
      }
      numberOfPops += 1;
      i += 1;
    }
    while (numberOfPops > 0) {
      data.mav.forecast.shift();
      numberOfPops -= 1;
    }
  }

  function getTopBase(value) {
    if (value && Number.isNaN(Number(value))) {
      return value;
    }
    return parseInt(value, 10) * 100 || null;
  }
  contents.polishAirmetValues = function (airmet) {
    switch (airmet.hazard_type) {
      case ChartConst.gairmetConfig.convectiveAirmet.key:
        airmet.real_altitude_min = airmet.properties.altitudeHi1;
        airmet.useSurfaceAsMinAlt = true;
        airmet.altitude_min = airmet.properties.altitudeHi1;
        airmet.altitude_max = airmet.properties.altitudeHi2;
        break;
      case ChartConst.gairmetConfig.icing.key:
        if (airmet.properties.base === ChartConst.gairmetConfig.frzgLvl.key2) {
          airmet.altitude_min = ChartConst.gairmetConfig.frzgLvl.key;
          airmet.real_altitude_min = ChartConst.gairmetConfig.frzgLvl.key;
        } else {
          airmet.real_altitude_min = getTopBase(airmet.properties.base);
          airmet.altitude_min = getTopBase(airmet.properties.base);
        }
        airmet.altitude_max = getTopBase(airmet.properties.top);
        break;
      default:
        airmet.real_altitude_min = getTopBase(airmet.properties.base);
        airmet.altitude_min = getTopBase(airmet.properties.base);
        airmet.altitude_max = getTopBase(airmet.properties.top);
    }
    if (airmet.isSigmet && airmet.hazard_type !== ChartConst.gairmetConfig.convectiveAirmet.key) {
      airmet.real_altitude_min = airmet.properties.altitudeLow1 || airmet.properties.altitudeHi1;
      if (
        airmet.properties.altitudeLow1 !== null
        && airmet.properties.altitudeLow1 !== 'undefined'
      ) {
        airmet.real_altitude_min = airmet.properties.altitudeLow1;
        airmet.altitude_min = airmet.properties.altitudeLow1;
      } else {
        airmet.real_altitude_min = airmet.altitudeHi1;
        airmet.altitude_min = airmet.properties.altitudeHi1;
      }
      airmet.altitude_max = airmet.properties.altitudeHi2;
    }
    if (airmet.isSigmet) {
      // If is sigmet then we have validTimeFrom, validTimeTo
      const vtf = new Date(airmet.properties.validTimeFrom);
      const vtt = new Date(airmet.properties.validTimeTo);
      airmet.validTimeFromLocalReal = vtf;
      airmet.validTimeToLocalReal = vtt;
      airmet.validFromLocal = vtf;
      airmet.expireOnLocal = vtt;
    } else {
      // If is gairmet then we have validTime
      const vt = new Date(airmet.properties.validTime);
      const { gairmetOffsetHoursPast, gairmetOffsetHoursFuture } = ChartConst.gairmetConfig;
      airmet.validTimeFromLocalReal = vt;
      airmet.validFromLocal = utilityService.subtractHours(new Date(vt), gairmetOffsetHoursPast);
      airmet.expireOnLocal = utilityService.addHours(new Date(vt), gairmetOffsetHoursFuture);
    }
  };

  function processAviationMet(airmet, settings, dataMode) {
    const temp = [];
    airmet.tooltip = {};
    if (dataMode !== 'mapview' && airmet.geometry && airmet.geometry.coordinates) {
      let tempCord = [];
      if (airmet.geometry.coordinates.length === 1) {
        const [coordToUse] = airmet.geometry.coordinates;
        tempCord = coordToUse;
      } else {
        tempCord = airmet.geometry.coordinates;
      }
      _forEach(tempCord, (v) => {
        temp.push([v[1], v[0]]);
      });
      airmet.geometry.coordinates = temp;
    }
    if (dataMode === 'mapview' && airmet.geometry.type === 'Polygon') {
      /**
       * This is done to make widning order of polygon right if it is anti-clockwise
       * https://bl.ocks.org/mbostock/a7bdfeb041e850799a8d3dce4d8c50c8
       */
      if (d3.geoArea(airmet) > 5) {
        airmet.geometry.coordinates[0] = airmet.geometry.coordinates[0].reverse();
      }
    }
    airmet.hazard_type = airmet.properties.hazard;
    contents.attachHazardTypeUniqueIdentifier(airmet);
    airmet.key = airmet.hazard_type_identifier;
    if (!airmet.airsigmet_type) {
      airmet.airsigmet_type = airmet.properties.airSigmetType;
    }
    airmet.tooltip.prefix = bnChartDataProcessService.getAirmetTooltipPrefix(airmet);
    airmet.hazardConfig = bnChartDataProcessService.getGairmetColor(airmet);
    airmet.colorIdentifier = airmet.hazardConfig.strokeColor;
    if (airmet.hazard_type === ChartConst.gairmetConfig.icing.key) {
      airmet.altitude_min = -1;
    }
    contents.polishAirmetValues(airmet);
    contents.normalizeAltitudeValue(airmet, settings, dataMode);
    airmet.altitude_min = Math.max(airmet.altitude_min, 0);
    airmet.altitude_max = Math.min(airmet.altitude_max, settings.maximumAltitudeAllowed);
  }
  // http://dev.buser.net/bnforecast/service/met
  contents.getBnforecastMet = async () => httpService.http_get(
    getServiceHostPreference() + AppConst.bnforecastMet,
  );
  contents.findClosestDate = function (startDate, dates) {
    // This function expects dates sorted
    // var hours;
    let i = 0;
    const minDifference = {
      index: null,
      hoursDifference: null,
      forecast_ts: null,
    };
    while (i < dates.length) {
      // hours = Math.abs(dates[i] - startDate) / 36e5;
      if (i === 0 || startDate >= dates[i]) {
        // minDifference.value = hours;
        minDifference.index = i;
        minDifference.forecast_ts = dates[i];
      }
      i += 1;
    }
    return minDifference;
  };

  contents.findClosestRecord = (airport, arrival_time) => {
    let i = 0;
    const stampsCollection = [];
    let closestDateObj;
    let closestRecord = null;
    if (airport.forecast) {
      _forEach(airport.forecast, (item) => {
        stampsCollection.push(item.forecast_ts_local);
      });
      closestDateObj = contents.findClosestDate(arrival_time, stampsCollection);
      while (i < airport.forecast.length) {
        if (airport.forecast[i].forecast_ts_local === closestDateObj.forecast_ts) {
          closestRecord = airport.forecast[i];
          break;
        }
        i += 1;
      }
      closestRecord.closestMetaData = closestDateObj;
    }
    return closestRecord;
  };
  function updateMavForecastByLampForFirst24HourIfAvailable(mavForecast, lampForecast) {
    if (lampForecast.frl !== null) {
      mavForecast.isUsingLamp = true;
      mavForecast.cig = lampForecast.cig || mavForecast.cig;
      mavForecast.cig_mapped = lampForecast.cig_mapped || mavForecast.cig_mapped;
      mavForecast.vis_mapped = lampForecast.vis_mapped || mavForecast.vis_mapped;
      mavForecast.vis = lampForecast.vis || mavForecast.vis;
      mavForecast.frl = lampForecast.frl || mavForecast.frl;
      mavForecast.cld = lampForecast.cld || mavForecast.cld;
      mavForecast.rlh = lampForecast.rlh || mavForecast.rlh;
      mavForecast.tmp = lampForecast.tmp || mavForecast.tmp;
      mavForecast.wsp = lampForecast.wsp || mavForecast.wsp;
      mavForecast.wdr = lampForecast.wdr || mavForecast.wdr;
      mavForecast.wgs = lampForecast.wgs || mavForecast.wgs;
      mavForecast.xwd = lampForecast.xwd || mavForecast.xwd;
      // Setting MAV st6 as null because all LAMP are used and LAMP does not have st6
      mavForecast.st6 = null;
      // Setting MAV q06 as null because all LAMP are used and LAMP does not have q06
      mavForecast.q06 = null;
      // Setting MAV st6 as null because all LAMP are used and LAMP does not have st6
      mavForecast.q06_mapped = null;
      // Dont replace st6 in MAV because it is not in LAMP but necessary to show
      // Common in MAV/LAMP
      mavForecast.sno = lampForecast.sno || mavForecast.sno;
      mavForecast.fzp = lampForecast.fzp || mavForecast.fzp;
      // Common in MAV/LAMP
      // OnlyIn MAV
      mavForecast.p06 = lampForecast.ppo || mavForecast.p06; // 6hr precip
      mavForecast.t06 = lampForecast.t01 || mavForecast.t01; // 6hr t-storm
      // OnlyIn MAV
      // OnlyIn LAMP
      // mavForecast.ppo = lampForecast.ppo; //Precip
      // mavForecast.t01 = lampForecast.cp2; //2hr t-storm
      // OnlyIn LAMP
    }
  }

  function useLampForMavStarting24HourMeteo(data) {
    let i = 0;
    let j;
    if (!data.lamp || !data.lamp.forecast || !data.mav || !data.mav.forecast) {
      return;
    }
    while (i < data.mav.forecast.length) {
      j = 0;
      while (j < data.lamp.forecast.length) {
        if (data.mav.forecast[i].forecast_ts === data.lamp.forecast[j].forecast_ts) {
          updateMavForecastByLampForFirst24HourIfAvailable(
            data.mav.forecast[i],
            data.lamp.forecast[j],
          );
          break;
        }
        j += 1;
      }
      i += 1;
    }
  }

  function updateForecastByMavIfLampMissing(closestMavRecord, lampForecast) {
    if (lampForecast.frl === null) {
      lampForecast.cig = closestMavRecord.cig;
      lampForecast.cig_mapped = closestMavRecord.cig_mapped;
      lampForecast.vis = closestMavRecord.vis;
      lampForecast.vis_mapped = closestMavRecord.vis_mapped;
      lampForecast.frl = closestMavRecord.frl;
      lampForecast.cld = closestMavRecord.cld;
      lampForecast.rlh = closestMavRecord.rlh;
      lampForecast.tmp = closestMavRecord.tmp;
      lampForecast.wsp = closestMavRecord.wsp;
      lampForecast.wdr = closestMavRecord.wdr;
      lampForecast.wgs = closestMavRecord.wgs;
      lampForecast.xwd = closestMavRecord.xwd;
      // Dont replace st6 in MAV because it is not in LAMP but necessary to show
      // Common in MAV/LAMP
      lampForecast.sno = closestMavRecord.sno;
      lampForecast.fzp = closestMavRecord.fzp;
      // Common in MAV/LAMP
      // OnlyIn MAV
      // lampForecast.q06 = closestMavRecord.q06;
      lampForecast.ppo = closestMavRecord.p06; // 6hr precip
      lampForecast.t01 = closestMavRecord.t06; // 6hr t-storm
    }
  }

  function useLampForMavStarting24HourRoute(data) {
    let k = 0;
    let i;
    let j;
    if (data.lamp) {
      while (k < data.lamp.length) {
        if (data.mav[k] && data.lamp[k].icao_id === data.mav[k].icao_id) {
          if (!data.mav[k].forecast || !data.lamp[k].forecast) {
            break;
          }
          i = 0;
          while (i < data.mav[k].forecast.length) {
            j = 0;
            while (j < data.lamp[k].forecast.length) {
              if (
                data.mav[k].forecast[i].forecast_ts_local.getTime()
                === data.lamp[k].forecast[j].forecast_ts_local.getTime()
              ) {
                updateMavForecastByLampForFirst24HourIfAvailable(
                  data.mav[k].forecast[i],
                  data.lamp[k].forecast[j],
                );
                break;
              }
              j += 1;
            }
            i += 1;
          }
        }
        k += 1;
      }
    }
  }

  function useMavRecordIfLampIsMissingRoute(data) {
    let k = 0;
    let i;
    let closestMavRecord;
    if (data.lamp) {
      while (k < data.mav.length) {
        if (data.mav[k] && data.lamp[k] && data.lamp[k].icao_id === data.mav[k].icao_id) {
          if (!data.mav[k].forecast || !data.lamp[k].forecast) {
            break;
          }
          i = 0;
          while (i < data.lamp[k].forecast.length) {
            closestMavRecord = contents.findClosestRecord(
              data.mav[k],
              data.lamp[k].forecast[i].forecast_ts_local,
            );
            if (closestMavRecord) {
              updateForecastByMavIfLampMissing(closestMavRecord, data.lamp[k].forecast[i]);
            }
            i += 1;
          }
        }
        k += 1;
      }
    }
  }

  function useMavRecordIfLampIsMissingMeteo(data) {
    let i = 0;
    let closestMavRecord;
    if (data.lamp && data.lamp.forecast && data.mav) {
      while (i < data.lamp.forecast.length) {
        closestMavRecord = contents.findClosestRecord(
          data.mav,
          data.lamp.forecast[i].forecast_ts_local,
        );
        if (closestMavRecord) {
          updateForecastByMavIfLampMissing(closestMavRecord, data.lamp.forecast[i]);
        }
        i += 1;
      }
    }
  }

  function positionAirportRight(data) {
    let i = 0;
    const outputUniqueAirports = [];
    const outputObj = [];
    let indexOfEnd;
    if (data) {
      while (i < data.length) {
        if (i === 0) {
          outputObj.push(data[i]);
          outputUniqueAirports.push(data[i].icao_id);
        } else if (i === data.length - 1) {
          indexOfEnd = outputUniqueAirports.indexOf(data[i].icao_id);
          if (indexOfEnd >= 0) {
            outputObj.splice(indexOfEnd, 1);
          }
          outputObj.push(data[i]);
          outputUniqueAirports.push(data[i].icao_id);
        } else if (outputUniqueAirports.indexOf(data[i].icao_id) === -1) {
          outputObj.push(data[i]);
          outputUniqueAirports.push(data[i].icao_id);
        }
        i += 1;
      }
    }
    return outputObj;
  }

  const isValidRouteProfileData = (data) => {
    /** Validate data for routeprofile */
    if (!data || !data.mav || !Array.isArray(data.mav) || data.mav.length === 1) {
      return false;
    }
    if (!data || !data.lamp || !Array.isArray(data.lamp) || data.lamp.length === 1) {
      return false;
    }
    return true;
  };

  contents.getAllRequiredData = async (settings, datamode) => {
    const data = await contents.getRequiredData(settings, datamode);
    if (
      !data
      || (data && !data.mav)
      || (data && data.mav && data.mav.length === 0)
      || (data && Object.keys(data).length === 0)
    ) {
      // For invalid airport service returns empty object
      return null;
    }
    if (datamode === AppConst.dataMode.routeprofile) {
      /** Validate data for routeprofile */
      if (isValidRouteProfileData(data) === false) {
        return null;
      }
    }

    if (data) {
      doRenamingCigVis(data, datamode);
      if (datamode !== AppConst.dataMode.meteogram) {
        // This will filter out mav of any airport that is present in route
        // But not present in airport.json
        data.mav = data.mav.filter(
          (item) => !!utilityService.getMatchedAirportObject(item.icao_id),
        );
        // This will filter out lamp of any airport that is present in route
        // But not present in airport.json
        data.lamp = data.lamp.filter(
          (item) => !!utilityService.getMatchedAirportObject(item.icao_id),
        );
        // this is happening for leafletmapview, routeprofile, gridview
        data.mav = positionAirportRight(data.mav);
        data.lamp = positionAirportRight(data.lamp);
      }
      if (
        !settings.isForMos
        && (datamode === AppConst.dataMode.routeprofile
          || datamode === AppConst.dataMode.leafletmapview
          || datamode === AppConst.dataMode.mapview
          || datamode === AppConst.dataMode.gridview)
      ) {
        refillLampStartEnd(data);
      }
      preProcessData(data.lamp, settings, datamode, ChartConst.products.lamp.name, data);
      preProcessData(data.mav, settings, datamode, ChartConst.products.mav.name, data);
      if (datamode === AppConst.dataMode.meteogram) {
        useMavRecordIfLampIsMissingMeteo(data);
        useLampForMavStarting24HourMeteo(data);
        meteoProcessData(data.lamp);
        meteoProcessData(data.mav);
      }
      if (
        datamode === AppConst.dataMode.routeprofile
        || datamode === AppConst.dataMode.leafletmapview
        || datamode === AppConst.dataMode.mapview
        || datamode === AppConst.dataMode.gridview
      ) {
        useMavRecordIfLampIsMissingRoute(data);
        useLampForMavStarting24HourRoute(data, datamode, settings);
      }
      removePastMavRecords(data);
      if (!settings.isForMos) {
        processAirSigmentData(data, settings, datamode);
      }
      getFactoryForDataMode(datamode).setFullData(data);
      if (ConfigDeviceSettings.settings.enableAlternateColorScheme) {
        contents.getFlightRuleData(settings, data);
      }
      //searchHistoryService.setAirportHistoryLog(datamode, data, settings);
      if (datamode === AppConst.dataMode.routeprofile) {
        // Elelvation data is used only in route profile
        const elevationData = await contents.getElevationData(data, settings);
        if (elevationData) {
          _forEach(elevationData, (item) => {
            item.elevation = utilityService.meterToFeet(item.elevation);
          });
          data.elevationData = elevationData;
        } else {
          data.elevationData = [];
        }
        return data;
      }
      if (datamode === AppConst.dataMode.leafletmapview || datamode === AppConst.dataMode.mapview) {
        // Get all METs for Map view if preferred MET provider is bnforecastAllMets
        // console.log(settings.departureTime);
        const bnforecastAllMets = await contents.getBnforecastMet();

        // console.log(settings.departureTime);
        if (bnforecastAllMets) {
          processAirSigmentData(bnforecastAllMets, settings, datamode);
          // this will filter out duplicate mets
          bnforecastAllMets.gairmet = utilityService.removeDuplicatesByKeyFromObjectInterface(
            bnforecastAllMets.gairmet,
            'duplicationKey',
          );
          bnforecastAllMets.airsigmet = utilityService.removeDuplicatesByKeyFromObjectInterface(
            bnforecastAllMets.airsigmet,
            'duplicationKey',
          );
        }
        data.bnforecastAllMets = bnforecastAllMets;
        return data;
      }
      return data;
    }
    return null;
  };

  contents.interpolateTmp = function (lowerBound, upperBound) {
    const feetForOneDegree = (upperBound.cig - lowerBound.cig) / (upperBound.tmp - lowerBound.tmp);
    const interpolatedValue = lowerBound.cig
      + (AppConst.temperatureUnits.fahrenheit.baseValue - lowerBound.tmp) * feetForOneDegree;
    return Math.floor(Math.abs(interpolatedValue));
  };

  function checkIfTransitionHappening(ref, tmp, baseValue) {
    if (ref) {
      return tmp > baseValue;
    }
    return tmp < baseValue;
  }
  contents.getAloftAltitudeIndexAtElevation = function (altitudeDistribution, el) {
    let j = 0;
    let validIndex;
    if (altitudeDistribution) {
      while (j < altitudeDistribution.length) {
        if (el < altitudeDistribution[j]) {
          validIndex = j - 1;
          break;
        }
        j += 1;
      }
    }
    return Math.max(0, validIndex);
  };
  contents.getLowerFreezeAltitude = function (altitudesData, settings) {
    let j = 0;
    const { baseValue } = AppConst.temperatureUnits.fahrenheit;
    let altitudeTmp;
    const lowerBound = {};
    const upperBound = {};
    const groundTempLessThanBase = !!(altitudesData[0].tmp < baseValue);
    const idxOfAirport = contents.getAloftAltitudeIndexAtElevation(
      DefConst[DefConst.aloftCloudIndexMapKey + settings.dataMode],
      settings.airportDetails.el,
    );
    let tmpAtAirportSfc;
    if (altitudesData && altitudesData[idxOfAirport]) {
      tmpAtAirportSfc = altitudesData[idxOfAirport].tmp;
    }
    // if temp at surface is below freezing i.e. 32 then we treat surface as freezing level
    if (tmpAtAirportSfc <= baseValue) {
      // This means by default if surface is at freezing then we use that for lower freezing
      altitudeTmp = settings.airportDetails.el;
    } else {
      while (j < altitudesData.length) {
        altitudeTmp = null;
        if (checkIfTransitionHappening(groundTempLessThanBase, altitudesData[j].tmp, baseValue)) {
          lowerBound.altIndex = j - 1;
          lowerBound.cig = DefConst[`aloftAltitudeMap${settings.dataMode}`][lowerBound.altIndex];
          // lowerBound.cig = settings.airportDetails.el;
          lowerBound.tmp = altitudesData[lowerBound.altIndex].tmp;
          upperBound.altIndex = j;
          upperBound.cig = DefConst[`aloftAltitudeMap${settings.dataMode}`][upperBound.altIndex];
          upperBound.tmp = altitudesData[upperBound.altIndex].tmp;
          altitudeTmp = contents.interpolateTmp(lowerBound, upperBound);
          break;
        }
        j += 1;
      }
    }
    // if altitudeTmp is not calculated yet then that means
    // that freezing is somewhere above maxAltitudeAllowed
    if (!altitudeTmp) {
      altitudeTmp = settings.maximumAltitudeAllowed + 1000;
    }
    return altitudeTmp;
  };

  contents.getUpperFreezeAltitude = function (altitudesData, settings) {
    let j = DefConst.altServiceOptions[
      Math.max(settings.maximumAltitudeAllowed, DefConst.defaultMaxAltAllowed)
    ];
    const { baseValue } = AppConst.temperatureUnits.fahrenheit;
    let altitudeTmp;
    const lowerBound = {};
    const upperBound = {};

    let skyTempLessThanBase;
    if (altitudesData && altitudesData[j]) {
      skyTempLessThanBase = !!(altitudesData[j].tmp < baseValue);
    }
    while (j >= 0) {
      altitudeTmp = null;
      if (
        altitudesData[j]
        && checkIfTransitionHappening(skyTempLessThanBase, altitudesData[j].tmp, baseValue)
        && DefConst[DefConst.aloftCloudIndexMapKey + settings.dataMode]
        && DefConst[DefConst.aloftCloudIndexMapKey + settings.dataMode][j + 1]
      ) {
        lowerBound.altIndex = j + 1;
        lowerBound.cig = DefConst[`aloftAltitudeMap${settings.dataMode}`][lowerBound.altIndex];
        lowerBound.tmp = altitudesData[lowerBound.altIndex].tmp;
        upperBound.altIndex = j;
        upperBound.cig = DefConst[`aloftAltitudeMap${settings.dataMode}`][upperBound.altIndex];
        upperBound.tmp = altitudesData[upperBound.altIndex].tmp;
        altitudeTmp = contents.interpolateTmp(lowerBound, upperBound);
        break;
      }
      j -= 1;
    }
    return altitudeTmp;
  };

  contents.normalizeTmpValue = function (altCigValue, altitudes, settings) {
    if (!altCigValue) {
      if (altitudes.length > 0 && altitudes[altitudes.length - 1].tmp) {
        altCigValue = altitudes[
          altitudes.length - 1].tmp < AppConst.temperatureUnits.fahrenheit.baseValue
          ? AppConst.extendedCeiling.min
          : settings.maximumAltitudeAllowed;
      } else {
        altCigValue = null;
      }
    }
    // This will make sure if altCig value is greater than max cig allowed
    // then tooltips uses correct value like : > 6000ft
    if (altCigValue !== null) {
      return Math.min(settings.maximumAltitudeAllowed, altCigValue);
    }
    return altCigValue;
  };
  contents.getInterpolatedTmpData = function (gribData, settings) {
    let altitudeTmpLowerBound;
    let altitudeTmpUpperBound;
    let elevation = 0;
    if (settings && settings.airportDetails && settings.airportDetails.el) {
      elevation = settings.airportDetails.el;
    }
    altitudeTmpUpperBound = contents.getUpperFreezeAltitude(gribData.altitudes, settings);
    altitudeTmpLowerBound = contents.getLowerFreezeAltitude(gribData.altitudes, settings);
    altitudeTmpLowerBound = contents.normalizeTmpValue(
      altitudeTmpLowerBound,
      gribData.altitudes,
      settings,
    );
    altitudeTmpUpperBound = contents.normalizeTmpValue(
      altitudeTmpUpperBound,
      gribData.altitudes,
      settings,
    );
    const aloftInterpolatedTmpData = {
      lowerBound: {
        x: gribData.model_ts,
        y: altitudeTmpLowerBound,
      },
      upperBound: {
        x: gribData.model_ts,
        y: altitudeTmpUpperBound,
      },
    };
    const { baseValue } = AppConst.temperatureUnits.fahrenheit;
    if (aloftInterpolatedTmpData.lowerBound.y < elevation) {
      aloftInterpolatedTmpData.lowerBound.y = elevation;
    }
    if (aloftInterpolatedTmpData.lowerBound.y === null) {
      if (
        gribData
        && gribData.altitudes
        && gribData.altitudes[0]
        && gribData.altitudes[0].tmp < baseValue
      ) {
        aloftInterpolatedTmpData.lowerBound.y = 0;
      } else if (
        gribData
        && gribData.altitudes
        && gribData.altitudes[0]
        && gribData.altitudes[0].tmp > baseValue
      ) {
        aloftInterpolatedTmpData.lowerBound.y = settings.maximumAltitudeAllowed;
      }
    }
    if (
      aloftInterpolatedTmpData.upperBound.y === null
      || aloftInterpolatedTmpData.upperBound.y < aloftInterpolatedTmpData.lowerBound.y
    ) {
      aloftInterpolatedTmpData.upperBound.y = aloftInterpolatedTmpData.lowerBound.y;
    }
    // console.log(aloftInterpolatedTmpData);
    return aloftInterpolatedTmpData;
  };
  contents.report9999Airport = function (projection, airport) {
    const sentryLogData = {
      exception: AppConst.exSubFor9999Rec.replace('[AIRPORT]', airport),
      exceptionMessage: projection,
      fullException: projection,
      locationView: window.location.href,
      airport,
    };
    reportError(sentryLogData);
  };
  contents.processAloftData = function (data, dataMode, projection, airport) {
    let i = 0;
    let j = 0;
    let tmpGrib;
    const aloftRefDate = data.model_ts;
    let hasAny9999 = false;
    // Remove first element i.e. empty object
    data.airports[0].grib.shift();
    // Fill in empty hours with pre-index data
    while (i < data.airports[0].grib.length) {
      tmpGrib = data.airports[0].grib[i];
      data.airports[0].grib[i] = {
        model_ts: utilityService.addHours(new Date(aloftRefDate), i),
      };
      data.airports[0].grib[i].altitudes = tmpGrib;
      j = 0;
      while (j < data.airports[0].grib[i].altitudes.length) {
        // if (data.airports[0].grib[i].altitudes[j].tmp) {
        //   data.airports[0].grib[i].altitudes[j].tmp = Math.ceil(data.airports[0].grib[i]
        //     .altitudes[j].tmp);
        // }
        if (data.airports[0].grib[i].altitudes[j].ugrd === AppConst.aloftValueToTreatAsNull) {
          hasAny9999 = true;
          data.airports[0].grib[i].altitudes[j].ugrd = null;
        }
        if (data.airports[0].grib[i].altitudes[j].vgrd === AppConst.aloftValueToTreatAsNull) {
          hasAny9999 = true;
          data.airports[0].grib[i].altitudes[j].vgrd = null;
        }
        if (data.airports[0].grib[i].altitudes[j].gust === AppConst.aloftValueToTreatAsNull) {
          hasAny9999 = true;
          data.airports[0].grib[i].altitudes[j].gust = null;
        }
        if (data.airports[0].grib[i].altitudes[j].tmp === AppConst.aloftValueToTreatAsNull) {
          hasAny9999 = true;
          data.airports[0].grib[i].altitudes[j].tmp = null;
        }
        if (data.airports[0].grib[i].altitudes[j].rh === AppConst.aloftValueToTreatAsNull) {
          hasAny9999 = true;
          data.airports[0].grib[i].altitudes[j].rh = null;
        }
        j += 1;
      }
      // if object is empty then use prev object altitude
      if (data.airports[0].grib[i].altitudes.length === 0) {
        data.airports[0].grib[i].altitudes = data.airports[0].grib[i - 1].altitudes;
      }
      i += 1;
    }
    if (hasAny9999 && dataMode === AppConst.dataMode.meteogram) {
      // We are doing this only for meteogram
      // To show "Wind and aloft data not available"
      data = null;
    }
    if (hasAny9999) {
      contents.report9999Airport(projection, airport);
    }
    return data;
  };
  contents.getAloftData = async (
    lat,
    lng,
    arrival_time,
    dataMode,
    airport,
    promiseIndex,
    maximumAltitudeAllowed,
  ) => {
    let processedAloftData;
    let i = 1;
    const cloudHeightMap = [100];
    if (utilityService.checkIfMakeAloftReq(lat, lng)) {
      const aldata = await httpService.http_get(
        httpService.getAloftProjection(lat, lng, maximumAltitudeAllowed),
        AppConst.cloudCacheEnabled ? AppConst.apiCaches.gribCache : false,
      );
      // simulate bad aloft
      // aldata.airports = [{ grib: [{}, {}] }];
      const data = aldata;
      if (data && (promiseIndex === 0 || !DefConst[DefConst.aloftCloudIndexMapKey + dataMode])) {
        while (i < data.airports[0].grib[1].length) {
          if (i < 20) {
            cloudHeightMap.push(
              Math.min(utilityService.meterToFeet(data.airports[0].grib[1][i].hgt), 45000),
            );
          }
          i += 1;
        }
        // cloudHeightMap.push(45500);
        data.aloftCloudIndexMap = cloudHeightMap;
        DefConst[DefConst.aloftCloudIndexMapKey + dataMode] = cloudHeightMap;
        utilityService.loadAloftAltitudeMap(cloudHeightMap, dataMode);
      }
      if (data && data.airports && data.airports[0]) {
        processedAloftData = contents.processAloftData(
          data,
          dataMode,
          httpService.getAloftProjection(lat, lng, maximumAltitudeAllowed),
          airport,
        );
        if (processedAloftData) {
          processedAloftData.arrival_time = arrival_time;
        }
        return processedAloftData;
      }
      // if (data !== null) {
      //     //if data is null that means, http request was canceled by
      // browser refresh. No need to report it
      //     // Throw error if aloft data not as expected
      //     throw new Error(httpService.getAloftProjection(lat, lng) +
      // ":" + JSON.stringify(data));
      // }
      return null;
    }
    return null;
  };
  contents.refineCeilingValueFromAloft = function (value, altitudes, sourceValue, settings) {
    const versionToUse = ConfigDeviceSettings.settings.apiVersionKey;
    if (versionToUse === 3) {
      return;
    }
    if (value && value.cig_real) {
      const ceilingRangeMapped = ChartConst.mavDefsMap.cigRange[value.cig_real];
      let i = 0;
      const ceilingAloftMapRange = [];
      let newCeilingValue;
      while (i < DefConst[DefConst.aloftCloudIndexMapKey + settings.dataMode].length - 1) {
        ceilingAloftMapRange.push([
          DefConst[DefConst.aloftCloudIndexMapKey + settings.dataMode][i],
          DefConst[DefConst.aloftCloudIndexMapKey + settings.dataMode][i + 1],
          i,
        ]);
        i += 1;
      }
      i = 0;
      const rangesMatching = [];
      while (i < ceilingAloftMapRange.length) {
        if (rangesMatching.length === 0 && ceilingAloftMapRange[i][1] > ceilingRangeMapped[0]) {
          rangesMatching.push(ceilingAloftMapRange[i]);
        } else if (
          rangesMatching.length === 1
          && ceilingRangeMapped[1] > ceilingAloftMapRange[i][1]
        ) {
          rangesMatching.push(ceilingAloftMapRange[i]);
        } else if (
          ceilingAloftMapRange[i][0] < ceilingRangeMapped[1]
          && ceilingAloftMapRange[i][1] > ceilingRangeMapped[1]
        ) {
          rangesMatching.push(ceilingAloftMapRange[i]);
        }
        i += 1;
      }
      i = 0;
      while (i < rangesMatching.length) {
        if (altitudes[rangesMatching[i][2]].rh > ChartConst.cloudThreshold.level1.threshold) {
          // range[3] = true;
          if (rangesMatching[i][0] > ceilingRangeMapped[0]) {
            const [newCigValue] = rangesMatching[i];
            newCeilingValue = newCigValue;
            break;
          }
        }
        i += 1;
      }
      if (newCeilingValue) {
        const elevationValue = value.y - value.tooltipValue;
        value.tooltipValue = newCeilingValue;
        value.y = newCeilingValue + elevationValue;
        if (sourceValue) {
          sourceValue.cig_mapped = value.tooltipValue;
        }
      }
      // round ceiling as per alt level
      value.tooltipValue = utilityService.roundCeilings(value.tooltipValue);
    }
  };

  function filterOutOverflowingdata(aloftData, chartData, settings, sourceData) {
    const viewRelevantData = [];
    const minRefDate = chartData.values[0].x;
    const maxRefDate = chartData.values[chartData.values.length - 1].x;
    let curentRecordRefDate;
    let j = 0;
    let k;
    const aloftTmpDataLowerBound = [];
    const aloftTmpDataUpperBound = [];
    let interPolatedData;
    while (j < aloftData.airports[0].grib.length) {
      curentRecordRefDate = aloftData.airports[0].grib[j].model_ts;
      if (
        curentRecordRefDate.getTime() >= minRefDate.getTime()
        && curentRecordRefDate.getTime() <= maxRefDate.getTime()
      ) {
        viewRelevantData.push(aloftData.airports[0].grib[j]);
        if (chartData && chartData.values) {
          k = 0;
          while (k < chartData.values.length) {
            if (chartData.values[k].x.getTime() === curentRecordRefDate.getTime()) {
              if (
                chartData.values[k].cig_real
                && aloftData.airports
                && aloftData.airports[0]
                && aloftData.airports[0].grib
                && aloftData.airports[0].grib[j]
                && aloftData.airports[0].grib[j].altitudes
              ) {
                contents.refineCeilingValueFromAloft(
                  chartData.values[k],
                  aloftData.airports[0].grib[j].altitudes,
                  sourceData.forecast[k],
                  settings,
                );
              }
            }
            k += 1;
          }
        }
        // Below code is used to debug grib freezing line
        // if (j === 27) {
        //     aloftData.airports[0].grib[j].altitudes.forEach(function (item) {
        //         console.log(item.tmp);
        //     });
        // }
        interPolatedData = contents.getInterpolatedTmpData(aloftData.airports[0].grib[j], settings);
        aloftTmpDataLowerBound.push(interPolatedData.lowerBound);
        aloftTmpDataUpperBound.push(interPolatedData.upperBound);
      }
      j += 1;
    }
    // aloftTmpDataLowerBound.forEach(function (item, key) {
    //     console.log(item.y, aloftTmpDataUpperBound[key].y);
    // });
    const filteredData = {
      viewRelevantData,
      aloftTmpDataLowerBound,
      aloftTmpDataUpperBound,
    };
    return filteredData;
  }

  contents.getViewRelatedAloftData = function (aloftData, chartData, settings, sourceData) {
    const filteredData = filterOutOverflowingdata(aloftData, chartData, settings, sourceData);
    aloftData.viewRelevantData = filteredData.viewRelevantData;
    aloftData.aloftTmpDataLowerBound = filteredData.aloftTmpDataLowerBound;
    aloftData.aloftTmpDataUpperBound = filteredData.aloftTmpDataUpperBound;
    return aloftData;
  };

  contents.processRouteAloftData = function (aloftData, settings) {
    let j = 0;
    let interpolatedData;
    const aloftTmpDataLowerBound = [];
    const aloftTmpDataUpperBound = [];
    while (j < aloftData.length) {
      settings.airportDetails = utilityService.getMatchedAirportObject(aloftData[j].icao_id);
      interpolatedData = contents.getInterpolatedTmpData(aloftData[j], settings);
      interpolatedData.lowerBound.el = settings.airportDetails.el;
      aloftTmpDataLowerBound.push(interpolatedData.lowerBound);
      aloftTmpDataUpperBound.push(interpolatedData.upperBound);
      j += 1;
    }
    delete settings.airportDetails;
    aloftData.aloftTmpDataLowerBound = aloftTmpDataLowerBound;
    aloftData.aloftTmpDataUpperBound = aloftTmpDataUpperBound;
    return aloftData;
  };

  contents.createRouteMap = function (data) {
    // Creating a MAP object
    const routeMap = {};
    _forEach(data, (item) => {
      if (!routeMap[item.icao_id]) {
        routeMap[item.icao_id] = {
          name: item.icao_id,
          elapsed_nm: item.elapsed_nm,
          childs: [item],
        };
      } else {
        routeMap[item.icao_id].childs.push(item);
      }
    });
    return routeMap;
  };

  contents.getDaysInData = function (data, objectKey, objectKeyForBoundary) {
    const daysForDayNight = [];
    let lastDay;
    let prevDayObject;
    let prevDay;
    let nextDayObject;
    let nextDay;
    _forEach(data, (item) => {
      if (item[objectKey]) {
        if (!lastDay) {
          lastDay = item[objectKey].getDate();
          daysForDayNight.push(item);
        } else if (item.forecast_ts.getDate() !== lastDay) {
          lastDay = item[objectKey].getDate();
          daysForDayNight.push(item);
        }
      }
    });
    if (daysForDayNight.length) {
      prevDay = new Date(
        new Date(daysForDayNight[0][objectKeyForBoundary]).setDate(
          daysForDayNight[0][objectKeyForBoundary].getDate() - 1,
        ),
      );
      prevDayObject = {
        forecast_ts: prevDay,
        lat: daysForDayNight[0].lat,
        lng: daysForDayNight[0].lng,
        sunMoonPositionData: utilityService.getSunMoonCalculation(
          prevDay,
          daysForDayNight[0].lat,
          daysForDayNight[0].lng,
        ),
      };
      nextDay = new Date(
        new Date(daysForDayNight[daysForDayNight.length - 1][objectKeyForBoundary]).setDate(
          daysForDayNight[daysForDayNight.length - 1][objectKeyForBoundary].getDate() + 1,
        ),
      );
      nextDayObject = {
        forecast_ts: nextDay,
        lat: daysForDayNight[daysForDayNight.length - 1].lat,
        lng: daysForDayNight[daysForDayNight.length - 1].lng,
        sunMoonPositionData: utilityService.getSunMoonCalculation(
          nextDay,
          daysForDayNight[daysForDayNight.length - 1].lat,
          daysForDayNight[daysForDayNight.length - 1].lng,
        ),
      };
      daysForDayNight.unshift(prevDayObject);
      daysForDayNight.push(nextDayObject);
    }
    return daysForDayNight;
  };
  contents.checkIfMetarDataExist = function (data) {
    const responseJson = x2jsService.xml_str2json(data);
    if (
      responseJson
      && responseJson.response
      && responseJson.response.data
      && responseJson.response.data._num_results !== '0'
    ) {
      return responseJson;
    }
    return false;
  };
  contents.checkIfMetsDataAvailable = function (data) {
    if (data) {
      return data;
    }
    return false;
  };
  contents.getApiUrl = function (sourceApiEndpoint, fallbackLevel) {
    const hostPref = getServiceHostPreference();
    const apiUrlReturn = AppConst.aviationFallbacks[fallbackLevel];
    return hostPref + apiUrlReturn + sourceApiEndpoint;
  };
  contents.aviationHandle = function (resolve, apiEndpoint, fallbackLevel, dataCheck) {
    let sentryLogData = {};
    // var api = contents.getApiUrl(apiEndpoint, fallbackLevel);
    // if (api.indexOf("SigmetJSON.php") > -1) {
    //     api = "static/data/sigmet.json";
    // }
    // if (apiEndpoint.indexOf("fore=0") > -1) {
    //     apiEndpoint = "static/data/gairmet0.json";
    // } else if (apiEndpoint.indexOf("fore=3") > -1) {
    //     apiEndpoint = "static/data/gairmet3.json";
    // } else if (apiEndpoint.indexOf("fore=6") > -1) {
    //     apiEndpoint = "static/data/gairmet6.json";
    // } else if (apiEndpoint.indexOf("fore=9") > -1) {
    //     apiEndpoint = "static/data/gairmet9.json";
    // } else if (apiEndpoint.indexOf("fore=12") > -1) {
    //     apiEndpoint = "static/data/gairmet12.json";
    // }
    httpService.http_get(contents.getApiUrl(apiEndpoint, fallbackLevel)).then((resData) => {
      let responseObj;
      try {
        responseObj = dataCheck(resData, fallbackLevel);
        if (responseObj) {
          resolve(responseObj);
        } else if (fallbackLevel < AppConst.aviationFallbacks.length - 1) {
          contents.aviationHandle(resolve, apiEndpoint, fallbackLevel + 1, dataCheck);
        } else {
          // if all fallback fails then log error
          sentryLogData = {
            exception: `All fallback API failed for ${apiEndpoint}`,
            explanation: 'Tried all fallback urls but none of them worked.',
            fullException: JSON.stringify(resData),
          };
          reportError(sentryLogData);
          resolve(null);
        }
      } catch (ex) {
        reportError(ex);
        console.log(ex);
      }
    });
  };
  contents.getAviationDataFromApi = async (
    apiEndpoint,
    checkingMethod,
    // eslint-disable-next-line no-promise-executor-return
  ) => new Promise((resolve) => contents.aviationHandle(resolve, apiEndpoint, 0, checkingMethod));
  contents.getSigmetsDataSource = async (api) => {
    const resolveObject = {};
    const data = await contents.getAviationDataFromApi(api, contents.checkIfMetsDataAvailable);
    resolveObject.sigmetData = data;
    return resolveObject;
  };
  contents.getGairmetDataSource = async (api) => {
    const resolveObject = {};
    const data = await contents.getAviationDataFromApi(api, contents.checkIfMetsDataAvailable);
    resolveObject.gairmetData = data;
    return resolveObject;
  };
  contents.getListOfMetsApiSources = () => {
    const fullListOfAviationsMetApi = [];
    _forEach(AppConst.aviationGairmetsIntervals, (value) => {
      fullListOfAviationsMetApi.push(
        AppConst.aviationMetEndPoint + AppConst.aviationMetGairmet + value,
      );
    });
    return fullListOfAviationsMetApi;
  };
  contents.getAviationMets = async (settings, dataMode) => {
    const promisses = [];
    let fullListOfAviationsMet = [];
    _forEach(contents.getListOfMetsApiSources(), (value) => {
      promisses.push(contents.getGairmetDataSource(value));
    });
    // for aviation .gov site we dont get SIGMETs data from .gov site because
    // .gov does not support CONNVECTIVE outlook
    // promisses.push(contents.getSigmetsDataSource(AppConst.aviationMetEndPoint
    //  + AppConst.aviationMetSigmet));
    const aviationMetsData = await Promise.all(promisses);
    _forEach(aviationMetsData, (value) => {
      if (value.gairmetData) {
        _forEach(value.gairmetData.features, (gMet) => {
          fullListOfAviationsMet.push(gMet);
        });
      }
      if (value.sigmetData) {
        value.sigmetData.features = [];
        _forEach(value.sigmetData.features, (sigMet) => {
          sigMet.isSigmet = true;
          fullListOfAviationsMet.push(sigMet);
        });
      }
    });
    if (fullListOfAviationsMet) {
      _forEach(fullListOfAviationsMet, (airmet) => {
        if (!airmet.processed && airmet.geometry) {
          airmet.processed = true;
          processAviationMet(airmet, settings, dataMode);
          airmet.duplicationKey = `${airmet.hazard_type_identifier}:${
            airmet.altitude_tooltip_text
          }:${airmet.validFromLocal}:${airmet.expireOnLocal}:${JSON.stringify(
            airmet.geometry.coordinates,
          )}`;
        }
      });
    }
    // this will filter out duplicate mets
    fullListOfAviationsMet = utilityService.removeDuplicatesByKeyFromObjectInterface(
      fullListOfAviationsMet,
      'duplicationKey',
    );
    return fullListOfAviationsMet;
  };

  contents.getAirportsListing = async () => {
    const data = await httpService.http_get(AppConst.airportsListingSource);
    return data?.filter((item) => item.icao_id) ?? [];
  };
  contents.getRadarSiteListing = async () => {
    const data = await httpService.http_get(AppConst.radarSiteSource);
    return data;
  };

  function getLocationsForElevation(airports, settings) {
    const airportPath = [];
    let i = 0;
    let startAirport = settings.start;
    let endAirport = settings.end;
    if (airports && airports.length) {
      if (startAirport !== airports[0].icao_id) {
        startAirport = airports[0].icao_id;
      }
      if (endAirport !== airports[airports.length - 1].icao_id) {
        endAirport = airports[airports.length - 1].icao_id;
      }
      const listOfAirports = [startAirport, settings.mid1, settings.mid2, endAirport];
      while (i < airports.length) {
        if (listOfAirports.indexOf(airports[i].icao_id) > -1) {
          airportPath.push({
            lat: airports[i].lat,
            lng: airports[i].lng,
          });
        }
        i += 1;
      }
    }
    return airportPath;
  }

  function getElevationKeyForLocalstorage(settings) {
    const listOfAirports = [settings.start, settings.mid1, settings.mid2, settings.end];
    return listOfAirports.filter((item) => item).join(':');
  }

  const requestElevation = (path) => new Promise((resolve) => {
    const elevator = new window.google.maps.ElevationService();
    elevator
      .getElevationAlongPath(
        {
          path,
          samples: AppConst.elevationSamples,
        },
        (elevations) => resolve(elevations),
        () => resolve(null),
      )
      .catch((ex) => {
        reportError(ex);
        resolve(null);
      });
  });

  const elevationHandle = async (airportdata, settings) => {
    const { lamp, mav } = airportdata;
    if (lamp?.length || mav?.length) {
      const pathLatLng = getLocationsForElevation(lamp || mav, settings);
      if (pathLatLng?.length) {
        const key = getElevationKeyForLocalstorage(settings);
        const elevationDataFromLs = getElevationFromLocalstorage(key);
        if (elevationDataFromLs) {
          return elevationDataFromLs;
        }
        const elevations = await requestElevation(pathLatLng);
        if (elevations?.length) {
          setElevationToLocalstorage(key, elevations);
          return elevations;
        }
      }
    }
    return null;
  };

  contents.getElevationData = (airportdata, settings) => new Promise((resolve) => {
    if (window.google) {
      elevationHandle(airportdata, settings).then(resolve);
      return;
    }
    setTimeout(() => {
      if (window.google) {
        elevationHandle(airportdata, settings).then(resolve);
      }
    }, 1000);
  });
  return contents;
};
export default bnChartDataService();
